import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Link } from 'react-router-dom';

const PropertyQuickModal = ({ property, onClose, formatPrice }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e?.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!property) return null;

  // Estimate rental yield: assume ~6% annual yield for rental, 5% for sale
  const estimateYield = () => {
    const price = parseInt(property?.price);
    if (!price || isNaN(price)) return null;
    if (property?.marketType === 'rental') {
      const monthlyRent = property?.rentalPeriod === 'yearly' ? price / 12 :
        property?.rentalPeriod === 'daily' ? price * 30 : price;
      const annualRent = monthlyRent * 12;
      // Estimate property value as ~150x monthly rent
      const estimatedValue = monthlyRent * 150;
      const yieldPct = ((annualRent / estimatedValue) * 100)?.toFixed(1);
      return yieldPct;
    }
    return '5.5'; // default for sale properties
  };

  const rentalYield = estimateYield();

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      {/* Modal Panel */}
      <div
        className="relative w-full sm:max-w-sm mx-auto bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up"
        onClick={(e) => e?.stopPropagation()}
        style={{ animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)' }}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
          aria-label="Fermer"
        >
          <Icon name="X" size={16} className="text-gray-600" />
        </button>

        {/* Property image strip */}
        <div className="relative h-36 overflow-hidden">
          <img
            src={property?.image}
            alt={property?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {/* Market type badge */}
          <span className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-white ${
            property?.marketType === 'rental' ? 'bg-blue-600' : 'bg-brand-gold'
          }`}>
            {property?.marketType === 'rental' ? 'À louer' : 'À vendre'}
          </span>
          {/* Title overlay */}
          <div className="absolute bottom-3 left-3 right-10">
            <p className="text-white font-heading font-semibold text-sm line-clamp-1">{property?.title}</p>
            <div className="flex items-center text-white/80 text-xs mt-0.5">
              <Icon name="MapPin" size={11} className="mr-1" />
              {property?.location}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {/* Price */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-text-secondary font-body uppercase tracking-wide mb-0.5">Prix</p>
              <p className="text-2xl font-heading font-bold text-brand-earth">
                {formatPrice(property?.price, property?.marketType, property?.rentalPeriod, property?.currency)}
              </p>
            </div>
            {/* Rental Yield */}
            {rentalYield && (
              <div className="text-right bg-green-50 rounded-xl px-3 py-2">
                <p className="text-xs text-green-700 font-body uppercase tracking-wide mb-0.5">Rendement</p>
                <p className="text-xl font-heading font-bold text-green-600">{rentalYield}%</p>
                <p className="text-xs text-green-600 font-body">annuel estimé</p>
              </div>
            )}
          </div>

          {/* Property features */}
          <div className="flex items-center gap-4 text-sm text-text-secondary mb-4 font-body border-t border-gray-100 pt-3">
            <div className="flex items-center gap-1">
              <Icon name="Bed" size={14} />
              <span>{property?.bedrooms} ch.</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Bath" size={14} />
              <span>{property?.bathrooms} sdb.</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Square" size={14} />
              <span>{property?.area} m²</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Link
              to={`/individual-property-page/${property?.id}`}
              className="flex-1"
            >
              <Button variant="outline" size="sm" fullWidth>
                Voir détails
              </Button>
            </Link>
            <Link
              to={`/consultation-booking-hub?property=${property?.id}`}
              className="flex-1"
            >
              <Button variant="default" size="sm" fullWidth iconName="Phone" iconPosition="left">
                Contacter
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (min-width: 640px) {
          @keyframes slideUp {
            from { transform: translateY(20px) scale(0.97); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
        }
      `}</style>
    </div>
  );
};

export default PropertyQuickModal;
